import { computed, ref, type ComputedRef, type Ref } from 'vue'

import { platformActionApi } from '../api/PlatformActionApi'
import { ConfirmActionFlow } from '../flows/ConfirmActionFlow'
import { LoadActionDetailFlow } from '../flows/LoadActionDetailFlow'
import type { FlowResult } from '../flows/platformActionFlow.types'
import { ProposeActionFlow } from '../flows/ProposeActionFlow'
import type {
  PlatformActionProposal,
  PlatformActionRequest,
  ProposeActionInput,
} from '../models/platformAction.types'
import type { ProposeBodyDto } from '../models/platformAction.wire'
import type {
  AdAccountCandidate,
  DuplicateActionTarget,
  PlatformActionError,
} from '../models/platformActionError'
import { isTerminalStatus } from '../service/actionStatus'
import {
  emptyProposeInput,
  validateProposeInput,
  type ProposeErrors,
} from '../service/proposeInput'
import { usePlatformActionStore } from '../stores/platformActionStore'

import { useActionPolling, type UseActionPollingOptions } from './useActionPolling'
import { useErrorMessage } from './useErrorMessage'

/** `form` → the user is filling it in · `preview` → the diff awaits approval · `result` → confirmed. */
export type ProposeStep = 'form' | 'preview' | 'result'

interface ProposeFlow {
  execute: (
    workspaceId: number,
    body: ProposeBodyDto,
  ) => Promise<FlowResult<PlatformActionProposal>>
}

interface ConfirmFlow {
  execute: (
    workspaceId: number,
    actionId: string,
    confirmToken: string,
  ) => Promise<FlowResult<PlatformActionRequest>>
}

interface PollFlow {
  execute: (
    workspaceId: number,
    actionId: string,
    options?: { silent?: boolean },
  ) => Promise<FlowResult<{ request: PlatformActionRequest }> | null>
}

export interface UseProposeActionOptions {
  proposeFlow?: ProposeFlow
  confirmFlow?: ConfirmFlow
  pollFlow?: PollFlow
  polling?: UseActionPollingOptions
}

export interface UseProposeAction {
  input: Ref<ProposeActionInput>
  step: ComputedRef<ProposeStep>
  fieldErrors: ComputedRef<ProposeErrors>
  error: ComputedRef<PlatformActionError | null>
  errorText: ComputedRef<string>
  /** Guardrail reasons from a 422 `GUARDRAIL_VIOLATION`, ready to list under the form. */
  violations: ComputedRef<string[]>
  /** The previewed payload no longer matches the token — the user must refresh the preview. */
  isStale: ComputedRef<boolean>
  /** The request this proposal collided with — the host offers to open it instead of re-proposing. */
  duplicate: ComputedRef<DuplicateActionTarget | null>
  /** Ad accounts an ambiguous create could have meant — the form offers them as a choice. */
  candidates: ComputedRef<AdAccountCandidate[]>
  /** Pick one of `candidates` and propose again with it. */
  chooseAdAccount: (workspaceId: number, adAccountId: string) => Promise<void>
  proposal: ComputedRef<PlatformActionProposal | null>
  result: ComputedRef<PlatformActionRequest | null>
  isBusy: ComputedRef<boolean>
  isPolling: ComputedRef<boolean>
  submit: (workspaceId: number) => Promise<void>
  confirm: (workspaceId: number) => Promise<boolean>
  /** Back to the form, keeping the typed input. */
  back: () => void
  reset: () => void
}

/**
 * The propose → preview → confirm → result handshake behind the "new action" modal.
 *
 * Two behaviours the ticket calls out explicitly live here:
 *   • an expired/invalid `confirm_token` (409) does NOT read as a failure — `isStale` flips and the
 *     modal offers "the preview is out of date, refresh it", which re-runs `submit`;
 *   • after confirming, the request is polled to a terminal status, so the shadow-mode
 *     `failed`/`ExecutionDisabled` outcome is what the user actually sees.
 */
export function useProposeAction(options: UseProposeActionOptions = {}): UseProposeAction {
  const store = usePlatformActionStore()
  const { errorMessage } = useErrorMessage()

  const proposeFlow = options.proposeFlow ?? new ProposeActionFlow({ api: platformActionApi })
  const confirmFlow =
    options.confirmFlow ?? new ConfirmActionFlow({ api: platformActionApi, store })
  const pollFlow = options.pollFlow ?? new LoadActionDetailFlow({ api: platformActionApi, store })

  const input = ref<ProposeActionInput>(emptyProposeInput())
  /**
   * The key that makes a re-submit idempotent, kept next to the body it was minted for.
   *
   * The window it has to cover is one modal session: `submit` fails on a timeout, the user presses
   * it again, and without a key the backend has no way to tell the retry from a second proposal —
   * it creates a second pending request against the same campaign. Sending the SAME key makes the
   * backend refuse with `DUPLICATE_ACTION_REQUEST` and name the request it already created.
   *
   * It is minted per body rather than per session because editing the budget and submitting again
   * is a genuinely different proposal, and per session rather than per content because the column
   * is globally unique FOREVER — a content-derived key would permanently bar re-proposing a change
   * after the first one was rejected. `reset()` (every modal open) drops it for the same reason.
   */
  const idempotency = ref<{ key: string; forBody: string } | null>(null)
  const step = ref<ProposeStep>('form')
  const fieldErrors = ref<ProposeErrors>({ payload: {} })
  const error = ref<PlatformActionError | null>(null)
  const proposal = ref<PlatformActionProposal | null>(null)
  const result = ref<PlatformActionRequest | null>(null)
  const busy = ref(false)

  const polling = useActionPolling(
    {
      load: async (workspaceId, actionId) => {
        const tick = await pollFlow.execute(workspaceId, actionId, { silent: true })
        if (tick?.ok !== true) return null
        result.value = tick.value.request
        return tick.value.request
      },
    },
    options.polling ?? {},
  )

  async function submit(workspaceId: number): Promise<void> {
    const validation = validateProposeInput(input.value)
    fieldErrors.value = validation.errors
    error.value = null
    if (!validation.valid || !validation.body) return

    // `validation.body` is built by one function in a fixed key order, so its serialisation is a
    // stable identity for "the same proposal" — compared before the key is added, never after.
    const forBody = JSON.stringify(validation.body)
    if (idempotency.value?.forBody !== forBody) {
      idempotency.value = { key: crypto.randomUUID(), forBody }
    }

    busy.value = true
    try {
      const proposed = await proposeFlow.execute(workspaceId, {
        ...validation.body,
        idempotency_key: idempotency.value.key,
      })
      if (!proposed.ok) {
        error.value = proposed.error
        // A rejected proposal invalidates any preview still on screen: never leave a stale diff
        // next to a fresh error.
        proposal.value = null
        step.value = 'form'
        return
      }
      proposal.value = proposed.value
      step.value = 'preview'
    } finally {
      busy.value = false
    }
  }

  async function confirm(workspaceId: number): Promise<boolean> {
    const current = proposal.value
    if (!current) return false

    busy.value = true
    error.value = null
    try {
      const confirmed = await confirmFlow.execute(
        workspaceId,
        current.request.id,
        current.confirmToken,
      )
      if (!confirmed.ok) {
        error.value = confirmed.error
        // The token is spent or stale — the preview cannot be approved, only rebuilt. Drop it and
        // return to the form, where `isStale` turns the submit button into "refresh preview".
        if (confirmed.error.requiresRepropose) {
          proposal.value = null
          step.value = 'form'
        }
        return false
      }
      result.value = confirmed.value
      step.value = 'result'
      if (!isTerminalStatus(confirmed.value.status)) {
        polling.start(workspaceId, confirmed.value.id)
      }
      return true
    } finally {
      busy.value = false
    }
  }

  /**
   * The backend refused because several ad accounts matched. Naming one makes it a different
   * proposal, so the idempotency key rotates on its own — the body changed.
   */
  async function chooseAdAccount(workspaceId: number, adAccountId: string): Promise<void> {
    input.value = { ...input.value, adAccountId }
    await submit(workspaceId)
  }

  /** Leave the preview without losing what the user typed — the modal's "back" button. */
  function back(): void {
    proposal.value = null
    error.value = null
    step.value = 'form'
  }

  function reset(): void {
    polling.stop()
    idempotency.value = null
    input.value = emptyProposeInput()
    step.value = 'form'
    fieldErrors.value = { payload: {} }
    error.value = null
    proposal.value = null
    result.value = null
    busy.value = false
  }

  return {
    input,
    step: computed(() => step.value),
    fieldErrors: computed(() => fieldErrors.value),
    error: computed(() => error.value),
    errorText: computed(() => (error.value ? errorMessage(error.value) : '')),
    violations: computed(() => error.value?.violations ?? []),
    isStale: computed(() => error.value?.requiresRepropose === true),
    duplicate: computed(() => error.value?.duplicate ?? null),
    candidates: computed(() => error.value?.candidates ?? []),
    chooseAdAccount,
    proposal: computed(() => proposal.value),
    result: computed(() => result.value),
    isBusy: computed(() => busy.value),
    isPolling: polling.isPolling,
    submit,
    confirm,
    back,
    reset,
  }
}
